import React, { useEffect, useState } from "react";
import { IconArrowLeft, IconHome, IconRotate } from "@tabler/icons-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { RouteConstants } from "../../constants/route-constant";
import { useEncoderCatalog } from "../../hooks/queries";
import type { EncoderTabId } from "../../types/encoder";
import {
  cn,
  encoderHref,
  normalizeTabId,
  parseEncoderPathname,
} from "../../utils/encoder";
import ClinicalCriteriasTab from "./ClinicalCriteriasTab";
import CmBuilderTab from "./CmBuilderTab";
import CodeEditorTab from "./CodeEditorTab";
import CodeSearchTab from "./CodeSearchTab";
import CodingGuideTab from "./CodingGuideTab";
import CptHcpcsEditorTab from "./CptHcpcsEditorTab";
import {
  CmHowItWorksControl,
  EncoderToastHost,
  EncoderUiProvider,
} from "./context";
import McdCoverageTab from "./McdCoverageTab";
import MsDrgGrouperTab from "./MsDrgGrouperTab";
import PcsBuilderTab from "./PcsBuilderTab";
import { EncoderToolsHome, EncoderToolsMenu } from "./toolGrid";

const TAB_RENDERERS: Record<
  EncoderTabId,
  (props: { procedure?: string }) => React.ReactNode
> = {
  "code-search": () => <CodeSearchTab />,
  "coding-guide": () => <CodingGuideTab />,
  "clinical-criteria": () => <ClinicalCriteriasTab />,
  "cm-explorer": () => <CmBuilderTab />,
  "pcs-explorer": () => <PcsBuilderTab />,
  "cpt-explorer": () => <CptHcpcsEditorTab />,
  editor: () => <CodeEditorTab />,
  "ms-drg-grouper": () => <MsDrgGrouperTab />,
  "mcd-coverage": ({ procedure }) => <McdCoverageTab procedure={procedure} />,
};

const EncoderWorkspaceInner: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useEncoderCatalog();
  const route = parseEncoderPathname(location.pathname);
  const activeTool: EncoderTabId | null = route === "home" ? null : route;
  const rawProcedure = searchParams.get("procedure") || undefined;
  const [mountedTabs, setMountedTabs] = useState<EncoderTabId[]>(() =>
    activeTool ? [activeTool] : []
  );
  const [resetToken, setResetToken] = useState(0);
  const [howToOpen, setHowToOpen] = useState(false);
  const [suppressProcedure, setSuppressProcedure] = useState(false);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const mapped = normalizeTabId(tabParam || "");
    if (mapped && route === "home") {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      if (mapped !== "mcd-coverage") next.delete("procedure");
      const search = next.toString();
      navigate(
        { pathname: encoderHref(mapped), search: search ? `?${search}` : "" },
        { replace: true }
      );
      return;
    }
    const base = RouteConstants.ROUTE_ENCODER;
    const trimmed =
      location.pathname.length > 1 && location.pathname.endsWith("/")
        ? location.pathname.slice(0, -1)
        : location.pathname;
    if (!trimmed.startsWith(`${base}/`)) return;
    const rest = trimmed.slice(base.length + 1).split("/")[0];
    if (rest && !normalizeTabId(rest)) {
      navigate(encoderHref(), { replace: true });
    }
  }, [location.pathname, navigate, route, searchParams]);

  useEffect(() => {
    if (!activeTool) return;
    setMountedTabs((current) =>
      current.includes(activeTool) ? current : [...current, activeTool]
    );
  }, [activeTool]);

  useEffect(() => {
    if (suppressProcedure && !rawProcedure) setSuppressProcedure(false);
  }, [rawProcedure, suppressProcedure]);

  useEffect(() => {
    setHowToOpen(false);
  }, [route]);

  const procedure =
    suppressProcedure || activeTool !== "mcd-coverage"
      ? undefined
      : rawProcedure;

  const goModules = () => navigate(RouteConstants.ROUTE_HOME);
  const goHome = () => navigate(encoderHref());

  const handleResetAll = () => {
    if (!activeTool) return;
    setHowToOpen(false);
    setResetToken((token) => token + 1);
    setMountedTabs([activeTool]);
    if (activeTool === "mcd-coverage" && rawProcedure) {
      setSuppressProcedure(true);
      navigate({ pathname: encoderHref("mcd-coverage"), search: "" }, { replace: true });
    }
  };

  const onHome = route === "home";

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden bg-muted/30">
      <div className="relative z-30 flex min-w-0 items-center border-b bg-background pt-1 pr-0 pb-1 pl-2">
        <button
          type="button"
          aria-label="Home"
          onClick={goModules}
          className="rounded-md p-1.5 text-slate-700 hover:bg-slate-100"
        >
          <IconHome size={16} />
        </button>
        <button
          type="button"
          aria-label={onHome ? "Back to modules" : "Back to encoder tools"}
          onClick={onHome ? goModules : goHome}
          className="ml-1 rounded-full bg-black p-1 text-white"
        >
          <IconArrowLeft size={24} />
        </button>
        <img
          src="/smart_encoder_logo.png"
          alt="Bulwark Encoder"
          className="ml-2 h-8 w-auto max-w-[min(280px,50vw)] shrink-0 object-contain object-left sm:h-10"
        />
        {activeTool === "cm-explorer" ? (
          <CmHowItWorksControl open={howToOpen} onOpenChange={setHowToOpen} />
        ) : null}
        {!onHome ? (
          <div className="ml-auto flex shrink-0 items-center gap-2 pr-2">
            <button
              type="button"
              aria-label="Reset all encoder tools"
              title="Clear every encoder tab"
              onClick={handleResetAll}
              className="inline-flex h-8 items-center gap-1 rounded-md bg-red-600 px-2.5 text-sm font-medium text-white hover:bg-red-700 sm:px-3"
            >
              <IconRotate size={14} />
              <span className="hidden sm:inline">Reset All</span>
            </button>
            <EncoderToolsMenu current={activeTool} />
          </div>
        ) : null}
      </div>

      <div
        className={cn(
          "min-h-0 flex-1 flex-col",
          onHome ? "flex" : "hidden"
        )}
      >
        <EncoderToolsHome />
      </div>

      {mountedTabs.map((tab) => (
        <div
          key={`${tab}-${resetToken}`}
          className={cn(
            "min-h-0 flex-1 flex-col",
            tab === activeTool ? "flex" : "hidden"
          )}
        >
          {TAB_RENDERERS[tab]({ procedure })}
        </div>
      ))}
      <EncoderToastHost />
    </div>
  );
};

const EncoderWorkspace: React.FC = () => (
  <EncoderUiProvider>
    <EncoderWorkspaceInner />
  </EncoderUiProvider>
);

export default EncoderWorkspace;
